import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, LocateFixed, MapPin } from "lucide-react";
import { ImageUploader } from "../components/Uploader";

const benefits = ["Professional public profile", "Customer discovery & lead generation", "Portfolio, services & E-Files publishing", "Analytics & subscription tools", "Verified badge after admin review"];

export function JoinExpert() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    professional_title: "", category: "3D Designer", about: "", location: "",
    experience_years: 0, skills: "", software: "", services: "", starting_price: 0, photo: "", website: "",
    portfolio_images: [],
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user && user !== false) api.get("/me/expert").then(r => { setMe(r.data); if (r.data) setForm(f => ({...f, ...r.data, skills: (r.data.skills||[]).join(", "), software: (r.data.software||[]).join(", "), services: (r.data.services||[]).join(", ")})); }); }, [user]);
  if (!user || user === false) return <PromptLogin next="/join/expert" role="EXPERT" />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/expert/apply", { ...form,
        skills: form.skills.split(",").map(s => s.trim()).filter(Boolean),
        software: form.software.split(",").map(s => s.trim()).filter(Boolean),
        services: form.services.split(",").map(s => s.trim()).filter(Boolean),
        experience_years: Number(form.experience_years) || 0,
        starting_price: Number(form.starting_price) || 0,
        portfolio: (form.portfolio_images || []).map((url, i) => ({ title: `Project ${i + 1}`, image: url })),
      });
      toast.success("Application submitted! Under review by admin.");
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="join-expert-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="tl-label mb-2">Join as</div>
      <h1 className="text-3xl sm:text-4xl font-display font-bold mb-2">Become a Trinity Expert</h1>
      <p className="text-[#475569] mb-6 max-w-2xl">Reach customers who need CAD, product design, and 3D modeling expertise. No commission — subscription-based platform.</p>
      {me && <div className="tl-card p-4 mb-6"><div className="tl-label mb-1">Status</div><div className="font-semibold text-[#0891B2]">{me.status}</div></div>}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="tl-card p-5 md:col-span-1">
          <div className="tl-label mb-3">Benefits</div>
          <ul className="space-y-2">{benefits.map(b => <li key={b} className="flex gap-2 text-sm text-[#475569]"><CheckCircle2 className="w-4 h-4 text-[#0891B2] shrink-0 mt-0.5" />{b}</li>)}</ul>
        </div>
        <form onSubmit={submit} className="tl-card p-5 md:col-span-2 flex flex-col gap-3">
          <select data-testid="ex-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="tl-input">
            {["3D Designer","CAD Designer","Product Designer","Mechanical Designer","3D Artist","Architectural Designer","Engineer","Consultant","Reverse Engineering","Other"].map(c => <option key={c}>{c}</option>)}
          </select>
          <input data-testid="ex-title" required placeholder="Professional title (e.g. Senior CAD Designer)" value={form.professional_title} onChange={e => setForm({...form, professional_title: e.target.value})} className="tl-input" />
          <textarea data-testid="ex-about" required rows={4} placeholder="About you & your expertise…" value={form.about} onChange={e => setForm({...form, about: e.target.value})} className="tl-input" />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Location (City, State)" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="tl-input" />
            <input type="number" placeholder="Experience (years)" value={form.experience_years} onChange={e => setForm({...form, experience_years: e.target.value})} className="tl-input" />
          </div>
          <input placeholder="Skills (comma separated)" value={form.skills} onChange={e => setForm({...form, skills: e.target.value})} className="tl-input" />
          <input placeholder="Software (comma separated)" value={form.software} onChange={e => setForm({...form, software: e.target.value})} className="tl-input" />
          <input placeholder="Services offered (comma separated)" value={form.services} onChange={e => setForm({...form, services: e.target.value})} className="tl-input" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Starting price ₹" value={form.starting_price} onChange={e => setForm({...form, starting_price: e.target.value})} className="tl-input" />
            <input placeholder="Website (optional)" value={form.website} onChange={e => setForm({...form, website: e.target.value})} className="tl-input" />
          </div>
          <input placeholder="Photo URL (optional)" value={form.photo} onChange={e => setForm({...form, photo: e.target.value})} className="tl-input" />
          <div>
            <div className="tl-label mb-2">Profile photo</div>
            <ImageUploader value={form.photo ? [form.photo] : []} onChange={(v) => setForm({...form, photo: v[0] || ""})} multiple={false} maxFiles={1} testId="ex-photo-uploader" />
          </div>
          <div>
            <div className="tl-label mb-2">Portfolio images</div>
            <ImageUploader value={form.portfolio_images || []} onChange={(v) => setForm({...form, portfolio_images: v})} multiple maxFiles={8} testId="ex-portfolio-uploader" />
          </div>
          <button data-testid="ex-submit" disabled={busy} className="tl-btn-primary justify-center">{busy ? "Submitting…" : (me ? "Update Application" : "Submit Application")}</button>
        </form>
      </div>
    </div>
  );
}

export function JoinPartner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    business_name: "", owner_name: "", description: "", location: "",
    lat: null, lng: null,
    technologies: "", materials: "", services: "", starting_price: 0, capacity: "", photo: "",
  });
  const [busy, setBusy] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);
  const [pinMsg, setPinMsg] = useState("");
  useEffect(() => { if (user && user !== false) api.get("/me/partner").then(r => { setMe(r.data); if (r.data) setForm(f => ({...f, ...r.data, technologies: (r.data.technologies||[]).join(", "), materials: (r.data.materials||[]).join(", "), services: (r.data.services||[]).join(", ")})); }); }, [user]);
  if (!user || user === false) return <PromptLogin next="/join/partner" role="PRINT_PARTNER" />;
  const pinLocation = () => {
    setPinMsg(""); setPinBusy(true);
    if (!navigator.geolocation) { setPinMsg("Geolocation not supported"); setPinBusy(false); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        // Best-effort reverse geocode via our backend's Nominatim proxy (using coords as a query works fine).
        let label = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        try {
          const { data } = await api.get(`/geo/search?q=${lat},${lng}`);
          if (data.results && data.results[0]) label = data.results[0].label;
        } catch {}
        setForm(f => ({ ...f, lat, lng, location: f.location || label }));
        setPinMsg("Location pinned ✓");
        setPinBusy(false);
      },
      (err) => { setPinMsg(err.code === 1 ? "Permission denied" : "Couldn't detect location"); setPinBusy(false); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const geocodeCity = async () => {
    if (!form.location.trim()) return;
    setPinMsg(""); setPinBusy(true);
    try {
      const { data } = await api.get(`/geo/search?q=${encodeURIComponent(form.location.trim())}`);
      const hit = (data.results || [])[0];
      if (!hit) { setPinMsg("Address not found — try a different one"); setPinBusy(false); return; }
      setForm(f => ({ ...f, lat: hit.lat, lng: hit.lng }));
      setPinMsg("Location pinned from address ✓");
    } catch { setPinMsg("Geocoding unavailable"); }
    finally { setPinBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/partner/apply", { ...form,
        technologies: form.technologies.split(",").map(s => s.trim()).filter(Boolean),
        materials: form.materials.split(",").map(s => s.trim()).filter(Boolean),
        services: form.services.split(",").map(s => s.trim()).filter(Boolean),
        starting_price: Number(form.starting_price) || 0,
        lat: form.lat != null ? Number(form.lat) : null,
        lng: form.lng != null ? Number(form.lng) : null,
      });
      toast.success("Application submitted! Under review.");
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="join-partner-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="tl-label mb-2">Join as</div>
      <h1 className="text-3xl sm:text-4xl font-display font-bold mb-2">Become a 3D Printer Owner</h1>
      <p className="text-[#475569] mb-8 max-w-2xl">List your printing capacity and reach customers who need FDM, SLA, or industrial printing.</p>
      {me && <div className="tl-card p-4 mb-6"><div className="tl-label mb-1">Status</div><div className="font-semibold text-[#0891B2]">{me.status}</div></div>}
      <form onSubmit={submit} className="tl-card p-5 flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="pp-business" required placeholder="Business name" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} className="tl-input" />
          <input required placeholder="Owner/Contact name" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} className="tl-input" />
        </div>
        <textarea data-testid="pp-desc" required rows={4} placeholder="Business description…" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="tl-input" />
        <div>
          <input required placeholder="Location (address, city, pincode)" value={form.location} onChange={e => setForm({...form, location: e.target.value, lat: null, lng: null})} className="tl-input" />
          <div className="mt-2 flex flex-wrap gap-2 items-center text-xs">
            <button data-testid="pp-pin-location" type="button" onClick={pinLocation} disabled={pinBusy} className="tl-btn-secondary disabled:opacity-50"><LocateFixed className="w-3.5 h-3.5" /> Pin from GPS</button>
            <button data-testid="pp-geocode" type="button" onClick={geocodeCity} disabled={pinBusy || !form.location.trim()} className="tl-btn-secondary disabled:opacity-50"><MapPin className="w-3.5 h-3.5" /> Pin from address</button>
            {form.lat != null && form.lng != null && (
              <span data-testid="pp-coords" className="tl-chip !bg-[#0891B2]/15 !text-[#0891B2] border-[#0891B2]/30">Pinned: {Number(form.lat).toFixed(3)}, {Number(form.lng).toFixed(3)}</span>
            )}
            {pinMsg && <span className="text-[#475569]">{pinMsg}</span>}
          </div>
          <p className="mt-1 text-[11px] text-[#64748B]">Pinning helps customers find you via "near me" search. Optional but recommended.</p>
        </div>
        <input placeholder="Technologies (FDM, SLA, Resin — comma separated)" value={form.technologies} onChange={e => setForm({...form, technologies: e.target.value})} className="tl-input" />
        <input placeholder="Materials (PLA, PETG, ABS — comma separated)" value={form.materials} onChange={e => setForm({...form, materials: e.target.value})} className="tl-input" />
        <input placeholder="Services (Prototype, Bulk, Custom — comma separated)" value={form.services} onChange={e => setForm({...form, services: e.target.value})} className="tl-input" />
        <div className="grid grid-cols-2 gap-3">
          <input type="number" placeholder="Starting price ₹" value={form.starting_price} onChange={e => setForm({...form, starting_price: e.target.value})} className="tl-input" />
          <input placeholder="Capacity (e.g. 500 parts/week)" value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} className="tl-input" />
        </div>
        <input placeholder="Cover photo URL" value={form.photo} onChange={e => setForm({...form, photo: e.target.value})} className="tl-input" />
        <div>
          <div className="tl-label mb-2">Cover photo</div>
          <ImageUploader value={form.photo ? [form.photo] : []} onChange={(v) => setForm({...form, photo: v[0] || ""})} multiple={false} maxFiles={1} testId="pp-photo-uploader" />
        </div>
        <button data-testid="pp-submit" disabled={busy} className="tl-btn-primary justify-center">{busy ? "Submitting…" : "Submit Application"}</button>
      </form>
    </div>
  );
}

function PromptLogin({ next, role }) {
  return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <h2 className="text-2xl font-display font-bold mb-2">Login required</h2>
      <p className="text-[#475569] mb-6">Create an account or login as {role === "EXPERT" ? "Expert" : "3D Printer Owner"} to continue.</p>
      <Link to={`/register?role=${role}`} className="tl-btn-primary inline-flex">Register <ArrowRight className="w-4 h-4" /></Link>
    </div>
  );
}
